import { protocolsQuery } from '@/api/protocols/protocolQueries';
import { FilterButton } from '@/components/common/FilterButton';
import { Section } from '@/components/common/Section';
import { Spinner } from '@/components/common/Spinner';
import { CardsContainer } from '@/components/dashboard/DashboardCard';
import { ProtocolCard } from '@/components/protocols/ProtocolCard';
import type { SelectedProtocolItem } from '@/components/protocols/ProtocolOrderFormCard';
import { ProtocolOrderFormCard } from '@/components/protocols/ProtocolOrderFormCard';
import { ProtocolsHeader } from '@/components/protocols/ProtocolsHeader';
import { ProtocolTypeCard } from '@/components/protocols/ProtocolTypeCard';
import type { ProtocolTypeItem } from '@/domain/Protocol';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const ProtocolsPage = () => {
  const { role } = useHasRole();
  const canShowOrderForm =
    role != null && role !== Role.GUEST && role !== Role.PACKER;

  const {
    data: protocols,
    isLoading,
    isError,
  } = useQuery({
    ...protocolsQuery(),
    retry: false,
  });
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<
    Array<SelectedProtocolItem>
  >([]);

  const protocolTypes = useMemo(() => {
    if (!protocols?.data) return [];
    const types = protocols.data.map((protocol) => protocol.type);
    return Array.from(new Set(types));
  }, [protocols?.data]);

  const selectedProtocol = useMemo(() => {
    if (!protocols?.data || !selectedFilter) return null;
    return (
      protocols.data.find((protocol) => protocol.type === selectedFilter) ??
      null
    );
  }, [selectedFilter, protocols?.data]);

  const selectedItemIds = useMemo(
    () => new Set(selectedItems.map((s) => s.protocolTypeItemId)),
    [selectedItems],
  );

  const getPriceId = useCallback(
    (item: ProtocolTypeItem): number | undefined => {
      const priceItem = item.item as { id: number } | undefined;
      return priceItem?.id;
    },
    [],
  );

  const getPriceCents = useCallback(
    (protocolTypeItem: ProtocolTypeItem): number => {
      const item = protocolTypeItem.item as
        | { price?: number; formatted_price?: number }
        | null
        | undefined;
      if (!item) return 0;
      if (typeof item.price === 'number') return item.price;
      if (typeof item.formatted_price === 'number')
        return Math.round(item.formatted_price * 100);
      return 0;
    },
    [],
  );

  const { completeProtocolPriceCents, selectedSupplementsPriceCents } =
    useMemo(() => {
      if (!selectedProtocol) {
        return {
          completeProtocolPriceCents: 0,
          selectedSupplementsPriceCents: 0,
        };
      }
      let complete = 0;
      const priceByProtocolTypeItemId = new Map<number, number>();
      selectedProtocol.protocol_types.forEach((pt) => {
        pt.items.forEach((protocolTypeItem) => {
          const cents = getPriceCents(protocolTypeItem);
          priceByProtocolTypeItemId.set(protocolTypeItem.id, cents);
          complete += cents;
        });
      });
      const selected = selectedItems.reduce(
        (sum, s) =>
          sum + (priceByProtocolTypeItemId.get(s.protocolTypeItemId) ?? 0),
        0,
      );
      return {
        completeProtocolPriceCents: complete,
        selectedSupplementsPriceCents: selected,
      };
    }, [selectedProtocol, selectedItems, getPriceCents]);

  const handleToggleItem = useCallback(
    (protocolTypeItem: ProtocolTypeItem) => {
      setSelectedItems((prev) => {
        if (prev.some((p) => p.protocolTypeItemId === protocolTypeItem.id))
          return prev.filter(
            (p) => p.protocolTypeItemId !== protocolTypeItem.id,
          );

        return [
          ...prev,
          {
            protocolTypeItemId: protocolTypeItem.id,
            itemId: protocolTypeItem.item_id,
            priceId: getPriceId(protocolTypeItem),
          },
        ];
      });
    },
    [getPriceId],
  );

  const handleAddCompleteProtocol = useCallback(() => {
    if (!selectedProtocol) return;

    const allItems: Array<SelectedProtocolItem> = [];
    selectedProtocol.protocol_types.forEach((pt) => {
      pt.items.forEach((item) => {
        allItems.push({
          protocolTypeItemId: item.id,
          itemId: item.item_id,
          priceId: getPriceId(item),
        });
      });
    });

    setSelectedItems((prev) => {
      const existingIds = new Set(prev.map((p) => p.protocolTypeItemId));
      const newItems = allItems.filter(
        (i) => !existingIds.has(i.protocolTypeItemId),
      );
      return [...prev, ...newItems];
    });
  }, [selectedProtocol, getPriceId]);

  useEffect(() => {
    if (protocols?.data && protocols.data.length > 0 && !selectedFilter) {
      const firstType = protocols.data[0].type;
      setSelectedFilter(firstType);
    }
  }, [protocols?.data, selectedFilter]);

  useEffect(() => {
    setSelectedItems([]);
  }, [selectedFilter]);

  const handleFilterToggle = (type: string) => {
    setSelectedFilter(type);
  };

  if (isLoading) {
    return (
      <main>
        <ProtocolsHeader
          bottomContent={
            <div className="flex items-center justify-center py-8">
              <Spinner className="size-8" />
            </div>
          }
        />
      </main>
    );
  }

  if (isError) {
    return (
      <main>
        <ProtocolsHeader
          bottomContent={
            <p className="text-destructive-foreground">
              There was an error loading the protocols.
            </p>
          }
        />
      </main>
    );
  }

  return (
    <main>
      <ProtocolsHeader
        bottomContent={
          <div>
            <div className="no-scrollbar -mr-4 mb-14 flex flex-nowrap gap-2.5 overflow-scroll lg:mr-0 lg:flex-wrap">
              {protocolTypes.map((type) => (
                <FilterButton
                  key={type}
                  isSelected={selectedFilter === type}
                  onClick={() => handleFilterToggle(type)}
                >
                  {type}
                </FilterButton>
              ))}
            </div>

            {selectedProtocol && <ProtocolCard protocol={selectedProtocol} />}
          </div>
        }
      />

      <Section containerClassName="pt-0 lg:pt-0 px-0 lg:px-6">
        {selectedProtocol && selectedProtocol.protocol_types.length > 0 && (
          <div className="flex flex-col gap-6">
            <CardsContainer className="gap-8">
              {selectedProtocol.protocol_types.map((protocolType) => (
                <ProtocolTypeCard
                  key={protocolType.id}
                  protocolType={protocolType}
                  selectedItemIds={
                    canShowOrderForm ? selectedItemIds : undefined
                  }
                  onToggleItem={canShowOrderForm ? handleToggleItem : undefined}
                />
              ))}
            </CardsContainer>

            {canShowOrderForm && (
              <CardsContainer>
                <ProtocolOrderFormCard
                  completeProtocolPriceCents={completeProtocolPriceCents}
                  selectedSupplementsPriceCents={selectedSupplementsPriceCents}
                  onAddCompleteProtocol={handleAddCompleteProtocol}
                  selectedItems={selectedItems}
                />
              </CardsContainer>
            )}
          </div>
        )}
      </Section>
    </main>
  );
};
